import React, {useCallback, useState} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from 'tss-react/mui';
import {FormattedMessage, useIntl} from 'react-intl';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CircularProgress from '@mui/material/CircularProgress';
import {OtpVerifySmsCode} from 'wobi-web-common';
import SignProgressStepper from '../components/SignProgressStepper';
import {sendCode, verifyCode} from '../api/signingApi';

const PHASE = {
  METHOD_SELECT: 'METHOD_SELECT',
  CODE_ENTRY: 'CODE_ENTRY',
};

const useStyles = makeStyles()((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    padding: '40px 20px',
    textAlign: 'center',
  },
  title: {
    fontSize: theme.typography.pxToRem(24),
    fontWeight: 700,
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.text.primary,
    marginBottom: 12,
  },
  subtitle: {
    fontSize: theme.typography.pxToRem(16),
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.text.secondary,
    marginBottom: 16,
    lineHeight: 1.6,
    maxWidth: 420,
  },
  maskedPhone: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 700,
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.text.primary,
    marginBottom: 32,
  },
  buttonsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: '100%',
    maxWidth: 340,
  },
  authButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: '16px 24px',
    borderRadius: 30,
    border: 'none',
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 600,
    fontFamily: theme.typography.fontFamily,
    cursor: 'pointer',
  },
  smsButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  whatsappButton: {
    // WhatsApp brand green — intentionally outside the theme palette.
    backgroundColor: '#25D366',
    color: theme.palette.common.white,
  },
  disabledButton: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  errorMessage: {
    color: theme.palette.error.main,
    fontSize: theme.typography.pxToRem(14),
    fontFamily: theme.typography.fontFamily,
    marginTop: 12,
  },
  codeEntryContainer: {
    width: '100%',
    maxWidth: 600,
    margin: '0 auto',
  },
  messageTop: {
    color: theme.palette.secondary.dark,
    // Reads backwards on purpose: `stylis-plugin-rtl` flips this to `ltr`, which is what keeps the
    // phone number inside the shared OTP component readable. That component takes no dir attribute.
    direction: 'rtl',
    fontSize: theme.typography.pxToRem(20),
    textAlign: 'center',
  },
  submitButton: {
    color: theme.palette.primary.contrastText,
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 'bold',
    width: '100%',
  },
  linkAlt: {
    color: theme.palette.secondary.light,
    fontSize: theme.typography.pxToRem(16),
  },
}));

const AuthScreen = ({signKey, authMethods, maskedPhone, onAuthenticated}) => {
  const {classes} = useStyles();
  const intl = useIntl();

  const [phase, setPhase] = useState(PHASE.METHOD_SELECT);
  const [sendLoading, setSendLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [error, setError] = useState('');
  const [sentMethod, setSentMethod] = useState('');

  const handleSendCode = useCallback(async (method) => {
    setError('');
    setSendLoading(true);
    setSentMethod(method);
    try {
      await sendCode(signKey, method);
      setPhase(PHASE.CODE_ENTRY);
    } catch (err) {
      const message = err?.message || '';
      const isRateLimited = message.includes('Too many') || message.includes('429');
      setError(intl.formatMessage({id: isRateLimited ? 'sign.auth.rateLimitError' : 'sign.auth.sendError'}));
    } finally {
      setSendLoading(false);
    }
  }, [signKey, intl]);

  const handleVerify = useCallback(async (data) => {
    setError('');
    setVerifyLoading(true);
    try {
      const result = await verifyCode(signKey, data.code);
      if (result?.authStatus === 'AUTHENTICATED') {
        await onAuthenticated();
      } else {
        setError(intl.formatMessage({id: 'sign.auth.verifyError'}));
      }
    } catch (err) {
      const message = err?.message || '';
      let messageId = 'sign.auth.verifyError';
      if (message.includes('wrongOtp')) {
        messageId = 'sign.auth.wrongCode';
      } else if (message.includes('maxAttempts')) {
        messageId = 'sign.auth.maxAttempts';
      }
      setError(intl.formatMessage({id: messageId}));
    } finally {
      setVerifyLoading(false);
    }
  }, [signKey, intl, onAuthenticated]);

  const handleResend = useCallback(async (method) => {
    setError('');
    try {
      await sendCode(signKey, method);
    } catch {
      setError(intl.formatMessage({id: 'sign.auth.sendError'}));
    }
  }, [signKey, intl]);

  const normalizedMethods = (authMethods || []).map((method) => method.toUpperCase());

  if (phase === PHASE.CODE_ENTRY) {
    return (
      <div>
        <SignProgressStepper activeStep={0} />
        <div className={classes.container}>
          <div className={classes.codeEntryContainer}>
            <OtpVerifySmsCode
              classes={classes}
              data={{phoneNumber: maskedPhone}}
              commonErrorMessage={error}
              clearError={() => setError('')}
              handleVerify={handleVerify}
              handleBack={() => setPhase(PHASE.METHOD_SELECT)}
              handleResend={handleResend}
              isLoading={verifyLoading}
              otpMethods={normalizedMethods}
              submitButtonText={intl.formatMessage({id: 'sign.auth.verifyButton'})}
              hideBackButton
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SignProgressStepper activeStep={0} />
      <div className={classes.container}>
        <div className={classes.title}>
          <FormattedMessage id='sign.auth.title' />
        </div>
        <div className={classes.subtitle}>
          <FormattedMessage id='sign.auth.description' />
        </div>
        {maskedPhone && <div className={classes.maskedPhone} dir='ltr'>{maskedPhone}</div>}
        <div className={classes.buttonsContainer}>
          {normalizedMethods.includes('SMS') && (
            <button
              type='button'
              className={`${classes.authButton} ${classes.smsButton} ${sendLoading ? classes.disabledButton : ''}`}
              onClick={() => handleSendCode('SMS')}
              disabled={sendLoading}
            >
              {sendLoading && sentMethod === 'SMS' ? <CircularProgress size={20} color='inherit' /> : <SmsOutlinedIcon />}
              <FormattedMessage id='sign.auth.sms' />
            </button>
          )}
          {normalizedMethods.includes('WHATSAPP') && (
            <button
              type='button'
              className={`${classes.authButton} ${classes.whatsappButton} ${sendLoading ? classes.disabledButton : ''}`}
              onClick={() => handleSendCode('WHATSAPP')}
              disabled={sendLoading}
            >
              {sendLoading && sentMethod === 'WHATSAPP'
                ? <CircularProgress size={20} color='inherit' />
                : <WhatsAppIcon />}
              <FormattedMessage id='sign.auth.whatsapp' />
            </button>
          )}
        </div>
        {error && <div className={classes.errorMessage}>{error}</div>}
      </div>
    </div>
  );
};

AuthScreen.propTypes = {
  authMethods: PropTypes.arrayOf(PropTypes.string),
  maskedPhone: PropTypes.string,
  onAuthenticated: PropTypes.func.isRequired,
  signKey: PropTypes.string.isRequired,
};

export default AuthScreen;
